import React, { useCallback } from 'react'

import { ScrollContainer } from '@factorialco/deprecated-design-system'
import {
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceReviewEvaluationQuery,
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceTargetWithPublishedEvaluationsFragment,
} from 'generated/resources/graphql'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'

import AnswersTable, {
  RenderCellArgs,
} from 'modules/performance_process/components/insights/Answers/common/AnswersTable'
import {
  Answer,
  extractQuestions,
  present,
  publishedAnswersWithTargetByReviewType,
} from 'modules/performance_process/components/insights/common/presenters'

import { EmptyState } from './EmptyState'

type PerformanceQuestionSingleLabel = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        PerformanceReviewEvaluationQuery['performance']['reviewEvaluation']
      >['answeredQuestionnaires']
    >['answeredQuestionnaireWithSections']
  >['content'][number]['questions'][number]['question']
>

interface TargetAnswersProps {
  reviewType: PerformanceReviewerStrategiesStrategyEnum
  target: PerformanceTargetWithPublishedEvaluationsFragment
  reviewQuestionnaireByReviewerStrategy: PerformanceQuestionnaireByReviewerStrategyFragment
  onSelectQuestion: (question: PerformanceQuestionSingleLabel) => void
  onSelectAnswer: (answer: Answer) => void
}

export const TargetAnswersTable: React.FC<TargetAnswersProps> = ({
  reviewType,
  target,
  reviewQuestionnaireByReviewerStrategy,
  onSelectQuestion,
  onSelectAnswer,
}) => {
  const { canUseRichTextEditor: withRichTextEditor, loading } = useRichTextEditor()

  // Get an object for the target with its answers for the selected reviewType
  const publishedAnswersWithTarget = publishedAnswersWithTargetByReviewType([target], reviewType)

  const { targets, answers, targetsWithReviewers } = present(
    publishedAnswersWithTarget, // Array of target objects with answers, already filtered by reviewType
    reviewType
  )

  // Get questions for the given reviewType
  const qualitativeQuestionsForStrategy = extractQuestions(
    reviewQuestionnaireByReviewerStrategy,
    reviewType
  )

  const renderCell = useCallback(
    ({ question, answer, ...props }: RenderCellArgs) => (
      <AnswersTable.AnswerCell
        {...props}
        answer={answer}
        onClick={() => {
          if (!answer) return
          onSelectQuestion(question)
          onSelectAnswer(answer)
        }}
      />
    ),
    [onSelectQuestion, onSelectAnswer]
  )

  if (loading) return null

  return (
    <ScrollContainer>
      {answers.length > 0 ? (
        <AnswersTable
          targets={targets}
          questions={qualitativeQuestionsForStrategy || []}
          answers={answers}
          targetsWithReviewers={targetsWithReviewers}
          renderCell={renderCell}
          withRichTextEditor={withRichTextEditor}
        />
      ) : (
        <EmptyState />
      )}
    </ScrollContainer>
  )
}
